import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Sparkles } from 'lucide-react';
// ===== Shopify Cart Permalink config =====
const MAIN_PRODUCT_VARIANT_ID = '44799276908630';
const UPSELL_PRODUCT_VARIANT_ID = '44800125534294';
const SHOPIFY_CART_DOMAIN = 'https://impulsodigital.vip/cart/';
const CHECKOUT_MAIN_ONLY = `${SHOPIFY_CART_DOMAIN}${MAIN_PRODUCT_VARIANT_ID}:1`;
const CHECKOUT_WITH_UPSELL = `${SHOPIFY_CART_DOMAIN}${MAIN_PRODUCT_VARIANT_ID}:1,${UPSELL_PRODUCT_VARIANT_ID}:1`;
function goToMainOnlyCheckout() {
  window.location.href = CHECKOUT_MAIN_ONLY;
}
function goToUpsellCheckout() {
  window.location.href = CHECKOUT_WITH_UPSELL;
}
interface UpsellModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export const UpsellModal: React.FC<UpsellModalProps> = ({
  isOpen,
  onClose
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);
  const benefits = ['Actividades listas para usar', 'Ideal para bajar pantallas', 'Estimula creatividad y concentración', 'Recibís todo en formato digital'];
  return <AnimatePresence data-id="element-220">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6" aria-modal="true" role="dialog" data-id="element-221">
          {/* Overlay */}
          <div className="absolute inset-0 bg-[#1a1530]/70 backdrop-blur-sm" onClick={onClose} aria-label="Cerrar" data-id="element-222" />

          {/* Modal Card */}
          <motion.div initial={{
        scale: 0.92,
        y: 20,
        opacity: 0
      }} animate={{
        scale: 1,
        y: 0,
        opacity: 1
      }} exit={{
        scale: 0.95,
        y: 10,
        opacity: 0
      }} transition={{
        type: 'spring',
        stiffness: 280,
        damping: 26
      }} className="relative w-full max-w-md sm:max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col" data-id="element-223">
            {/* Close button */}
            <button onClick={onClose} className="absolute top-3 right-3 z-20 w-9 h-9 flex items-center justify-center rounded-full bg-[#F4EDDF] hover:bg-[#EADFC8] text-[#24114A] shadow-sm transition-colors" aria-label="Cerrar" data-id="element-224">
              <X size={18} strokeWidth={2.5} data-id="element-225" />
            </button>

            {/* Scrollable content */}
            <div className="overflow-y-auto px-5 pt-5 pb-5 sm:px-7 sm:pt-6 sm:pb-7" data-id="element-226">
              {/* Badge */}
              <div className="flex justify-center mb-3" data-id="element-227">
                <div className="inline-flex items-center gap-1.5 bg-[#FFF3CC] text-[#B8841A] px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider border border-[#F3C85B]" data-id="element-228">
                  <Sparkles size={12} fill="currentColor" data-id="element-229" />
                  Oferta por única vez
                </div>
              </div>

              {/* Title */}
              <h2 className="font-rounded font-bold text-xl sm:text-2xl text-[#24114A] text-center leading-tight mb-4" data-id="element-230">
                Antes de finalizar tu compra…
              </h2>

              {/* Product row: image + price side by side */}
              <div className="relative flex items-center gap-3 bg-[#FFF9EC] rounded-2xl p-3 mb-3 border border-[#F3C85B]/40" data-id="element-231">
                {/* Discount badge */}
                <div className="absolute -top-2 -right-2 bg-[#E97849] text-white text-[11px] font-extrabold uppercase px-2.5 py-1 rounded-full shadow-md rotate-6 border-2 border-white z-10" data-id="element-232">
                  -70% OFF
                </div>
                <img src="https://cdn.magicpatterns.com/uploads/2KuCf95nezJXoFevuD6DpJ/ChatGPT_Image_1_jun_2026__23_57_40-removebg-preview.png" alt="Pack Premium de Actividades Sin Pantallas" className="w-32 sm:w-36 h-auto flex-shrink-0 drop-shadow-md" data-id="element-233" />
                <div className="flex-1 min-w-0" data-id="element-234">
                  <div className="inline-block bg-[#24114A] text-[#FFD95A] text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full mb-1" data-id="element-235">
                    +100 actividades
                  </div>
                  <h3 className="font-rounded font-bold text-[#24114A] text-sm sm:text-base leading-tight mb-1" data-id="element-236">
                    Pack Premium de Actividades Sin Pantallas
                  </h3>
                  <p className="text-xs text-[#5D5670] font-semibold mb-0.5" data-id="element-237">
                    Antes: <span className="line-through" data-id="element-238">$20.000</span>
                  </p>
                  <p className="font-rounded font-extrabold text-[#E97849] text-2xl sm:text-3xl leading-none" data-id="element-239">
                    $3.990
                  </p>
                </div>
              </div>

              {/* Pain-point hook */}
              <div className="flex items-center justify-center gap-1.5 mb-4 bg-[#EAF6EE] border border-[#3DBB63]/30 rounded-full px-3 py-2" data-id="element-240">
                <span className="text-[#1f7a40] text-xs sm:text-sm font-extrabold text-center leading-snug" data-id="element-241">
                  📵 Menos pantallas, más momentos en familia
                </span>
              </div>

              {/* Benefits */}
              <ul className="space-y-2 mb-4" data-id="element-242">
                {benefits.map((b, idx) => <li key={idx} className="flex items-start gap-2.5 text-[#24114A] text-sm font-semibold" data-id="element-243">
                    <div className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-[#3DBB63] flex items-center justify-center" data-id="element-244">
                      <Check className="text-white w-3 h-3" strokeWidth={4} data-id="element-245" />
                    </div>
                    <span className="leading-snug" data-id="element-246">{b}</span>
                  </li>)}
              </ul>

              {/* Primary CTA */}
              <button onClick={goToUpsellCheckout} className="w-full bg-[#E97849] hover:bg-[#F08A5A] active:scale-[0.98] text-white font-rounded font-bold text-base sm:text-lg py-4 px-5 rounded-2xl shadow-[0_10px_24px_-8px_rgba(233,120,73,0.55)] border-b-[4px] border-[#c45f37] transition-all duration-200 mb-3" data-id="element-247">
                Sí, agregar por solo $3.990
              </button>

              {/* Secondary CTA */}
              <button onClick={goToMainOnlyCheckout} className="w-full text-[#5D5670] hover:text-[#24114A] font-bold text-base sm:text-lg underline underline-offset-4 py-3 transition-colors" data-id="element-248">
                No gracias, continuar solo con mi compra
              </button>

              {/* Trust micro-copy */}
              <p className="text-center text-[11px] text-[#5D5670]/80 mt-3 leading-snug" data-id="element-249">
                Se agrega automáticamente y recibís todo junto en formato
                digital.
              </p>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
};